import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { currentPeriod, periodLabel, rows, type ID, type LeavePolicy, type LeavePolicyRules, type LeaveType } from '@hrms/shared';
import { api } from '../../../lib/api';
import { humanize } from '../../../lib/date';
import { fieldErrors } from '../../../lib/errors';
import { useMut } from '../../../lib/query';
import { CrudList } from '../components/CrudList';
import { PeriodSelect } from '../components/misc';
import { useOrgOptions } from '../components/orgOptions';
import {
  Alert, Badge, Button, Card, Checkbox, Columns, ConfirmDialog, DataTable, Dot, Grid, IconButton, Modal, PageHeader, Row, Screen, Select, Stack, Switch, Tabs, Text, TextField,
} from '../../../ui';

/** Buttons default to alignSelf flex-start; bottom-align them with labelled fields. */
const END = { alignSelf: 'flex-end' as const };

type TabKey = 'policies' | 'types' | 'processing';
type YearEndRow = Awaited<ReturnType<typeof api.leave.yearEnd>>['rows'][number];

const DEFAULT_RULES: LeavePolicyRules = {
  accrual: { frequency: 'monthly', amount: 1, prorate_on_joining: true },
  max_balance: null, carry_forward_max: null, encashable: false, encash_max: null, sandwich_rule: false, allow_half_day: true,
  min_notice_days: 0, max_consecutive_days: null, allow_negative: false, requires_hr_approval: false,
  applicable: { grade_ids: [], location_ids: [], gender: null, min_service_days: 0 },
};

export function describeRules(r: LeavePolicyRules): string {
  const parts: string[] = [];
  if (r.accrual.frequency === 'none') parts.push('No automatic accrual');
  else parts.push(`Accrues ${r.accrual.amount} day(s) ${r.accrual.frequency}${r.accrual.prorate_on_joining ? ', prorated for new joiners' : ''}`);
  if (r.max_balance != null) parts.push(`capped at ${r.max_balance}`);
  parts.push(r.carry_forward_max != null ? `up to ${r.carry_forward_max} carried forward` : 'balance lapses at year end');
  if (r.encashable) parts.push(`encashable${r.encash_max != null ? ` up to ${r.encash_max}` : ''}`);
  if (r.sandwich_rule) parts.push('sandwich rule applies');
  if (r.allow_half_day === false) parts.push('no half days');
  if (r.min_notice_days) parts.push(`${r.min_notice_days} days' notice`);
  if (r.max_consecutive_days) parts.push(`max ${r.max_consecutive_days} consecutive days`);
  if (r.allow_negative) parts.push('negative balance allowed');
  if (r.requires_hr_approval) parts.push('HR approval required');
  if (r.applicable?.gender) parts.push(`${r.applicable.gender} employees only`);
  if (r.applicable?.min_service_days) parts.push(`after ${r.applicable.min_service_days} days of service`);
  return parts.join('; ') + '.';
}

const mergeRules = (r?: Partial<LeavePolicyRules>): LeavePolicyRules => ({
  ...DEFAULT_RULES,
  ...r,
  accrual: { ...DEFAULT_RULES.accrual, ...r?.accrual },
  applicable: { ...DEFAULT_RULES.applicable, ...r?.applicable },
});
const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v.replace(/[^0-9.]/g, '')));

function PolicyBuilder({ policy, types, onClose }: { policy: Partial<LeavePolicy>; types: LeaveType[]; onClose: () => void }) {
  const org = useOrgOptions();
  const [p, setP] = useState<Partial<LeavePolicy>>(() => ({ is_active: true, ...policy, rules: mergeRules(policy.rules) }));
  const [json, setJson] = useState<string | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const r = p.rules!;
  const setR = (patch: Partial<LeavePolicyRules>) => setP({ ...p, rules: { ...r, ...patch } });
  const setA = (patch: Partial<LeavePolicyRules['accrual']>) => setR({ accrual: { ...r.accrual, ...patch } });
  const setAp = (patch: Partial<NonNullable<LeavePolicyRules['applicable']>>) => setR({ applicable: { ...r.applicable, ...patch } });
  const save = useMut(() => (p.id ? api.leave.policies.update(p.id, p) : api.leave.policies.create(p)), {
    invalidate: [['leave-policies']],
    success: p.id ? 'Policy saved (new version created)' : 'Policy created',
    onSuccess: onClose,
    onError: (e) => setErrs(fieldErrors(e)),
  });
  const toggleId = (list: ID[] | undefined, id: ID) => (list?.includes(id) ? list.filter((x) => x !== id) : [...(list ?? []), id]);
  const n = (k: keyof LeavePolicyRules, label: string, hint?: string) => (
    <TextField key={k} label={label} hint={hint} error={errs[`rules.${k}`]} keyboardType="decimal-pad" placeholder="No limit" value={r[k] == null ? '' : String(r[k])} onChangeText={(v) => setR({ [k]: numOrNull(v) } as Partial<LeavePolicyRules>)} />
  );
  const b = (k: keyof LeavePolicyRules, label: string, description?: string) => <Switch key={k} label={label} description={description} value={!!r[k]} onChange={(v) => setR({ [k]: v } as Partial<LeavePolicyRules>)} />;
  const submit = () => {
    const local: Record<string, string> = {};
    if (!p.name?.trim()) local.name = 'Name is required';
    if (!p.leave_type_id) local.leave_type_id = 'Pick a leave type';
    if (Object.keys(local).length) return setErrs(local);
    save.mutate(undefined);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={p.id ? `Edit policy · v${p.version}` : 'New leave policy'}
      width={760}
      testID="policy-builder"
      footer={
        <>
          <Button title="Cancel" onPress={onClose} />
          <Button variant="primary" title="Save policy" loading={save.isPending} onPress={submit} testID="policy-save" />
        </>
      }
    >
      <Grid min={220} gap={14}>
        <TextField label="Policy name" required error={errs.name} value={p.name ?? ''} onChangeText={(name) => setP({ ...p, name })} testID="policy-name" />
        <Select label="Leave type" required error={errs.leave_type_id} value={p.leave_type_id ?? null} options={types.map((t) => ({ value: t.id, label: `${t.name} (${t.code})` }))} onChange={(leave_type_id) => setP({ ...p, leave_type_id })} />
      </Grid>
      <Switch label="Active" value={!!p.is_active} onChange={(is_active) => setP({ ...p, is_active })} />
      <Alert tone="info">{describeRules(r)}</Alert>
      {json === null ? (
        <>
          <Card title="Accrual">
            <Grid min={180} gap={14}>
              <Select<LeavePolicyRules['accrual']['frequency']> label="Frequency" value={r.accrual.frequency} onChange={(frequency) => setA({ frequency })} options={(['none', 'monthly', 'quarterly', 'yearly'] as const).map((f) => ({ value: f, label: humanize(f) }))} />
              <TextField label="Days per period" error={errs['rules.accrual.amount']} keyboardType="decimal-pad" disabled={r.accrual.frequency === 'none'} value={String(r.accrual.amount ?? '')} onChangeText={(v) => setA({ amount: Number(v.replace(/[^0-9.]/g, '')) || 0 })} />
              <View style={{ paddingTop: 26 }}>
                <Checkbox label="Prorate on joining" value={!!r.accrual.prorate_on_joining} onChange={(v) => setA({ prorate_on_joining: v })} />
              </View>
            </Grid>
          </Card>
          <Card title="Balance, carry-forward & encashment">
            <Grid min={180} gap={14}>
              {n('max_balance', 'Max balance')}
              {n('carry_forward_max', 'Carry forward max', 'Blank = lapse all')}
              {n('encash_max', 'Encash max days')}
            </Grid>
            <Stack gap={10} style={{ marginTop: 12 }}>
              {b('encashable', 'Encashable', 'At year end and on exit')}
              {b('allow_negative', 'Allow negative balance')}
            </Stack>
          </Card>
          <Card title="Application rules">
            <Grid min={180} gap={14}>
              {n('min_notice_days', 'Min notice (days)')}
              {n('max_consecutive_days', 'Max consecutive days')}
            </Grid>
            <Stack gap={10} style={{ marginTop: 12 }}>
              {b('sandwich_rule', 'Sandwich rule', 'Count weekly offs / holidays between leave days')}
              {b('allow_half_day', 'Allow half-day leave')}
              {b('requires_hr_approval', 'Requires additional HR approval')}
            </Stack>
          </Card>
          <Card title="Applicability">
            <Grid min={180} gap={14}>
              <Select<'' | 'male' | 'female'> label="Gender" value={r.applicable?.gender ?? ''} onChange={(g) => setAp({ gender: g || null })} options={[{ value: '', label: 'All' }, { value: 'female', label: 'Female only' }, { value: 'male', label: 'Male only' }]} />
              <TextField label="Min service (days)" keyboardType="number-pad" value={String(r.applicable?.min_service_days ?? 0)} onChangeText={(v) => setAp({ min_service_days: Number(v.replace(/\D/g, '')) || 0 })} />
            </Grid>
            <View style={{ marginTop: 12 }}>
              <Columns ratio={[1, 1]}>
                <Stack gap={6}>
                  <Text variant="label">Grades {r.applicable?.grade_ids?.length ? `(${r.applicable.grade_ids.length})` : '(all)'}</Text>
                  {org.grades.map((g) => (
                    <Checkbox key={g.value} label={g.label} value={!!r.applicable?.grade_ids?.includes(g.value)} onChange={() => setAp({ grade_ids: toggleId(r.applicable?.grade_ids, g.value) })} />
                  ))}
                  {!org.grades.length && <Text variant="small">No grades defined</Text>}
                </Stack>
                <Stack gap={6}>
                  <Text variant="label">Locations {r.applicable?.location_ids?.length ? `(${r.applicable.location_ids.length})` : '(all)'}</Text>
                  {org.locations.map((g) => (
                    <Checkbox key={g.value} label={g.label} value={!!r.applicable?.location_ids?.includes(g.value)} onChange={() => setAp({ location_ids: toggleId(r.applicable?.location_ids, g.value) })} />
                  ))}
                  {!org.locations.length && <Text variant="small">No locations defined</Text>}
                </Stack>
              </Columns>
            </View>
          </Card>
          <Row justify="flex-end">
            <Button size="sm" variant="ghost" icon="edit" title="Edit rules JSON" onPress={() => setJson(JSON.stringify(r, null, 2))} />
          </Row>
        </>
      ) : (
        <Card
          title="Rules JSON"
          actions={
            <Button
              size="sm"
              title="Apply & back to form"
              onPress={() => {
                try {
                  setR(mergeRules(JSON.parse(json)));
                  setJson(null);
                  setErrs({});
                } catch {
                  setErrs({ json: 'Invalid JSON' });
                }
              }}
            />
          }
        >
          <TextField accessibilityLabel="Rules JSON" multiline style={{ fontFamily: 'monospace', fontSize: 12, minHeight: 320 }} value={json} onChangeText={setJson} error={errs.json} />
        </Card>
      )}
    </Modal>
  );
}

function PoliciesTab() {
  const types = useQuery({ queryKey: ['leave-types', 'all'], queryFn: () => api.leave.types.list({ per_page: 100 }) });
  const q = useQuery({ queryKey: ['leave-policies'], queryFn: () => api.leave.policies.list({ per_page: 200 }) });
  const [edit, setEdit] = useState<Partial<LeavePolicy> | null>(null);
  const [del, setDel] = useState<ID | null>(null);
  const remove = useMut((id: ID) => api.leave.policies.remove(id), { invalidate: [['leave-policies']], success: 'Policy deleted', onSuccess: () => setDel(null) });
  return (
    <Stack gap={12}>
      <Row>
        <Button size="sm" variant="primary" icon="plus" title="New policy" onPress={() => setEdit({})} testID="policy-new" />
      </Row>
      <DataTable<LeavePolicy>
        rows={rows(q.data)}
        loading={q.isLoading}
        onRowPress={(p) => setEdit(p)}
        emptyTitle="No leave policies"
        columns={[
          { key: 'name', title: 'Policy', width: 160, flex: 2 },
          { key: 'type', title: 'Leave type', width: 140, sortValue: (p) => p.leave_type?.name, render: (p) => <Row gap={6}><Dot color={p.leave_type?.color ?? '#888'} /><Text>{p.leave_type?.name ?? `#${p.leave_type_id}`}</Text></Row> },
          { key: 'summary', title: 'Rules', width: 300, flex: 4, sortable: false, render: (p) => <Text size={12}>{describeRules(mergeRules(p.rules))}</Text> },
          { key: 'version', title: 'Ver', width: 60, align: 'right' },
          { key: 'is_active', title: 'Status', width: 90, sortValue: (p) => (p.is_active ? 1 : 0), render: (p) => <Badge tone={p.is_active ? 'success' : 'neutral'}>{p.is_active ? 'Active' : 'Inactive'}</Badge> },
          { key: 'act', title: '', width: 56, flex: 0, sortable: false, render: (p) => <IconButton icon="trash" size="sm" label={`Delete ${p.name}`} onPress={() => setDel(p.id)} /> },
        ]}
      />
      {edit && <PolicyBuilder policy={edit} types={rows(types.data)} onClose={() => setEdit(null)} />}
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={() => del && remove.mutate(del)} title="Delete policy?" message="Existing balances are kept; no further accruals will run for this policy." danger confirmLabel="Delete" loading={remove.isPending} />
    </Stack>
  );
}

function ProcessingTab() {
  const now = new Date();
  const [period, setPeriod] = useState(currentPeriod());
  const [year, setYear] = useState(String(now.getFullYear() - (now.getMonth() < 3 ? 1 : 0)));
  const [preview, setPreview] = useState<YearEndRow[] | null>(null);
  const [confirm, setConfirm] = useState(false);
  const accrue = useMut(() => api.leave.accrue(period), { invalidate: [['leave']], success: (r) => `${r.credited} balances credited for ${periodLabel(period)}` });
  const ye = useMut((dry: boolean) => api.leave.yearEnd({ year: Number(year), dry_run: dry }), {
    invalidate: [['leave']],
    onSuccess: (r, dry) => {
      if (dry) setPreview(r.rows);
      else {
        setPreview(null);
        setConfirm(false);
      }
    },
    success: (_r, dry) => (dry ? 'Dry run complete' : 'Year-end processing complete'),
  });
  const totals = (preview ?? []).reduce((a, x) => ({ cf: a.cf + x.carry_forward, en: a.en + x.encashed, la: a.la + x.lapsed }), { cf: 0, en: 0, la: 0 });
  return (
    <Stack>
      <Card title="Monthly accrual" subtitle="Credits leave per each policy's accrual rule. Safe to re-run — periods already credited are skipped.">
        <Row align="flex-end">
          <View style={{ minWidth: 200 }}>
            <PeriodSelect value={period} onChange={setPeriod} />
          </View>
          <Button variant="primary" icon="play" title="Run accrual" loading={accrue.isPending} onPress={() => accrue.mutate(undefined)} testID="leave-accrue" style={END} />
        </Row>
      </Card>
      <Card title="Year-end processing" subtitle="Applies carry-forward, encashment and lapse. Always preview with a dry run first.">
        <Row align="flex-end">
          <TextField label="Leave year" keyboardType="number-pad" value={year} onChangeText={(v) => { setYear(v.replace(/\D/g, '').slice(0, 4)); setPreview(null); }} containerStyle={{ width: 120 }} />
          <Button icon="eye" title="Dry run" loading={ye.isPending && ye.variables === true} disabled={year.length !== 4} onPress={() => ye.mutate(true)} testID="leave-yearend-dry" style={END} />
          <Button variant="danger" icon="lock" title="Commit" style={END} disabled={!preview} onPress={() => setConfirm(true)} />
        </Row>
        {preview && (
          <Stack gap={10} style={{ marginTop: 12 }}>
            <Row gap={8}>
              <Badge tone="info">{`${preview.length} balances`}</Badge>
              <Badge tone="success">{`Carry fwd ${totals.cf}`}</Badge>
              <Badge tone="warning">{`Encashed ${totals.en}`}</Badge>
              <Badge tone="danger">{`Lapsed ${totals.la}`}</Badge>
            </Row>
            <DataTable
              rows={preview}
              keyExtractor={(r, i) => `${r.employee}-${r.leave_type}-${i}`}
              emptyTitle="Nothing to process"
              columns={[
                { key: 'employee', title: 'Employee', width: 180, flex: 2 },
                { key: 'leave_type', title: 'Leave type', width: 120 },
                { key: 'balance', title: 'Balance', width: 90, align: 'right' },
                { key: 'carry_forward', title: 'Carry fwd', width: 90, align: 'right' },
                { key: 'encashed', title: 'Encashed', width: 90, align: 'right' },
                { key: 'lapsed', title: 'Lapsed', width: 90, align: 'right' },
              ]}
            />
          </Stack>
        )}
      </Card>
      <Text variant="small">Encashment amounts flow into payroll inputs for the next run.</Text>
      <ConfirmDialog open={confirm} onClose={() => setConfirm(false)} onConfirm={() => ye.mutate(false)} loading={ye.isPending && ye.variables === false} title={`Commit year-end for ${year}?`} message="Balances will be carried forward, encashed or lapsed. This cannot be undone." danger confirmLabel="Commit" />
    </Stack>
  );
}

/** Leave types, policy rules builder and periodic processing. */
export function LeavePoliciesScreen() {
  const [tab, setTab] = useState<TabKey>('policies');
  return (
    <Screen testID="leave-policies">
      <PageHeader title="Leave policies" subtitle="Leave types, policy rules and periodic processing." />
      <Tabs<TabKey> value={tab} onChange={setTab} tabs={[{ key: 'policies', label: 'Policies' }, { key: 'types', label: 'Leave types' }, { key: 'processing', label: 'Accrual & year-end' }]} />
      {tab === 'policies' && <PoliciesTab />}
      {tab === 'types' && (
        <CrudList<LeaveType>
          embedded
          title="Leave types"
          singular="Leave type"
          resource={api.leave.types}
          queryKey={['leave-types']}
          columns={[
            { key: 'code', title: 'Code', width: 90, render: (t) => <Row gap={6}><Dot color={t.color} /><Text>{t.code}</Text></Row>, sortValue: (t) => t.code },
            { key: 'name', title: 'Name', width: 160, flex: 2 },
            { key: 'is_paid', title: 'Paid', width: 120, sortValue: (t) => (t.is_paid ? 1 : 0), render: (t) => (t.is_paid ? <Badge tone="success">Paid</Badge> : <Badge tone="danger">Unpaid (LOP)</Badge>) },
            { key: 'is_comp_off', title: 'Comp-off', width: 90, sortValue: (t) => (t.is_comp_off ? 1 : 0), render: (t) => (t.is_comp_off ? 'Yes' : '—') },
          ]}
          fields={[
            { key: 'code', label: 'Code', required: true, placeholder: 'CL' },
            { key: 'name', label: 'Name', required: true, placeholder: 'Casual leave' },
            { key: 'color', label: 'Colour', type: 'color', default: '#4f46e5' },
            { key: 'is_paid', label: 'Paid leave', type: 'checkbox', default: true },
            { key: 'is_comp_off', label: 'Comp-off type', type: 'checkbox' },
          ]}
        />
      )}
      {tab === 'processing' && <ProcessingTab />}
    </Screen>
  );
}
